import React, { useState } from 'react';
import { db } from '../firebaseConfig';
import { collection, addDoc, updateDoc, doc, Timestamp } from 'firebase/firestore';
import { useAuth } from '../AuthContext';
import AddressInput from './AddressInput';
import { toast } from 'react-toastify';

function ClubForm({ onCancel, onSuccess, initialData }) {
    const { user } = useAuth();
    const isEditMode = !!initialData;

    const [formData, setFormData] = useState({
        name: initialData?.name || '',
        description: initialData?.description || '',
        schedule: initialData?.schedule || [{ day: 'Thursday', time: '18:00', endTime: '', label: '' }],
        website: initialData?.website || '',
        isKidFriendly: initialData?.isKidFriendly || false,
        isBeginnerFriendly: initialData?.isBeginnerFriendly || false,
    });
    const [location, setLocation] = useState(initialData ? { address: initialData.address, coordinates: initialData.location } : null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleScheduleChange = (index, field, value) => {
        const newSchedule = [...formData.schedule];
        newSchedule[index][field] = value;
        setFormData({ ...formData, schedule: newSchedule });
    };

    const addScheduleSlot = () => {
        setFormData({
            ...formData,
            schedule: [...formData.schedule, { day: 'Sunday', time: '12:00', endTime: '', label: '' }]
        });
    };

    const removeScheduleSlot = (index) => {
        if (formData.schedule.length > 1) {
            const newSchedule = formData.schedule.filter((_, i) => i !== index);
            setFormData({ ...formData, schedule: newSchedule });
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.name.trim() || !location) {
            toast.error('Please enter a club name and select a location.');
            return;
        }

        setIsSubmitting(true);

        const clubData = {
            name: formData.name,
            description: formData.description,
            website: formData.website,
            schedule: formData.schedule,
            // Backward compatibility
            meetingDay: formData.schedule[0]?.day || 'Thursday',
            meetingTime: formData.schedule[0]?.time || '18:00',

            address: location.address,
            location: location.coordinates,
            updatedAt: Timestamp.now(),
            isKidFriendly: formData.isKidFriendly,
            isBeginnerFriendly: formData.isBeginnerFriendly,
        };

        if (!isEditMode) {
            clubData.createdAt = Timestamp.now();
            clubData.ownerId = user.uid;
            clubData.ownerName = user.displayName;
            clubData.photoURL = user.photoURL;
            clubData.type = 'club';
        }

        try {
            if (isEditMode) {
                await updateDoc(doc(db, 'clubs', initialData.id), clubData);
                toast.success('Club updated successfully!');
            } else {
                await addDoc(collection(db, 'clubs'), clubData);
                toast.success('Club registered successfully!');
            }
            if (onSuccess) onSuccess();
        } catch (error) {
            console.error('Error saving club:', error);
            toast.error(`Failed to save club: ${error.message}`);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="bg-white p-4 rounded-lg shadow-sm border border-indigo-100 mb-4">
            <h3 className="font-bold text-lg mb-4 text-indigo-700">{isEditMode ? 'Edit Club' : 'Register a Club / Meetup'}</h3>
            <form onSubmit={handleSubmit} className="space-y-4">
                <input
                    type="text"
                    placeholder="Group Name (e.g. Sunday Coffee Chess)"
                    className="w-full border rounded px-3 py-2"
                    value={formData.name}
                    onChange={e => setFormData({ ...formData, name: e.target.value })}
                    required
                />

                <AddressInput onLocationSelect={setLocation} initialValue={location?.address} />

                <div className="bg-gray-50 p-3 rounded border border-gray-200">
                    <label className="block text-xs font-bold text-gray-500 mb-2 uppercase">Weekly Schedule</label>
                    {formData.schedule.map((slot, index) => (
                        <div key={index} className="flex flex-col gap-2 mb-3 bg-white p-2 rounded border border-gray-100">
                            <div className="flex gap-2 items-center flex-wrap">
                                <select
                                    className="border rounded px-2 py-1 text-sm bg-white flex-1 min-w-[100px]"
                                    value={slot.day}
                                    onChange={e => handleScheduleChange(index, 'day', e.target.value)}
                                >
                                    {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map(day => (
                                        <option key={day} value={day}>{day}</option>
                                    ))}
                                </select>

                                <div className="flex items-center gap-1">
                                    <input
                                        type="time"
                                        className="border rounded px-2 py-1 text-sm w-24"
                                        value={slot.time}
                                        onChange={e => handleScheduleChange(index, 'time', e.target.value)}
                                        title="Start Time"
                                    />
                                    <span className="text-gray-400 text-xs">-</span>
                                    <input
                                        type="time"
                                        className="border rounded px-2 py-1 text-sm w-24"
                                        value={slot.endTime || ''}
                                        onChange={e => handleScheduleChange(index, 'endTime', e.target.value)}
                                        title="End Time"
                                    />
                                </div>
                                <button
                                    type="button"
                                    onClick={() => removeScheduleSlot(index)}
                                    className="text-gray-400 hover:text-red-500 px-2 ml-auto"
                                    title="Remove time"
                                    disabled={formData.schedule.length === 1}
                                >
                                    ✕
                                </button>
                            </div>
                        </div>
                    ))}
                    <button
                        type="button"
                        onClick={addScheduleSlot}
                        className="text-xs text-indigo-600 font-medium hover:underline flex items-center gap-1 mt-2"
                    >
                        + Add another time
                    </button>
                </div>
                <input
                    type="url"
                    placeholder="Website / Social Link (Optional)"
                    className="w-full border rounded px-3 py-2"
                    value={formData.website}
                    onChange={e => setFormData({ ...formData, website: e.target.value })}
                />

                <textarea
                    placeholder="Description / Notes"
                    className="w-full border rounded px-3 py-2"
                    rows="2"
                    value={formData.description}
                    onChange={e => setFormData({ ...formData, description: e.target.value })}
                />

                <div className="flex gap-4">
                    <div className="flex items-center">
                        <input
                            type="checkbox"
                            id="isKidFriendly"
                            checked={formData.isKidFriendly}
                            onChange={(e) => setFormData({ ...formData, isKidFriendly: e.target.checked })}
                            className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                        />
                        <label htmlFor="isKidFriendly" className="ml-2 block text-sm text-gray-900">
                            Kid-Friendly
                        </label>
                    </div>

                    <div className="flex items-center">
                        <input
                            type="checkbox"
                            id="isBeginnerFriendly"
                            checked={formData.isBeginnerFriendly}
                            onChange={(e) => setFormData({ ...formData, isBeginnerFriendly: e.target.checked })}
                            className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
                        />
                        <label htmlFor="isBeginnerFriendly" className="ml-2 block text-sm text-gray-900">
                            Beginner-Friendly
                        </label>
                    </div>
                </div>

                <div className="flex gap-2">
                    <button
                        type="button"
                        onClick={onCancel}
                        className="flex-1 bg-gray-100 text-gray-600 py-2 rounded hover:bg-gray-200"
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        disabled={isSubmitting}
                        className="flex-1 bg-indigo-600 text-white py-2 rounded hover:bg-indigo-700"
                    >
                        {isSubmitting ? 'Saving...' : (isEditMode ? 'Update Club' : 'Register Club')}
                    </button>
                </div>
            </form>
        </div>
    );
}

export default ClubForm;
